import React from 'react';
import { Mui } from '@osu/react-ui';
import { Link, useLocation } from 'react-router-dom';
import { PROGRAMS } from '../../util/constants';
import { deriveProgramTypeFromPathname, hasDualProgramAccess } from '../../util/scholars/scholarsScope';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';

// Default landing route for each side when switching (has543). Only cross-program users see this
// switcher, so there is no student variant here.
const HONORS_LANDING   = "/";
const SCHOLARS_LANDING  = "/scholars/queue";

// has543 — Honors/Scholars program-type switcher (in-session navigation only).
// Visibility (has543v2): shown for ANY user with a Honors-side role AND a Scholars-side role — not
// just dual-admins. Single-side users never see it and only ever see their own side.
// Scope/active side is path-driven (`/scholars/*` = Scholars). Selecting a side ONLY navigates —
// it does NOT change the persisted default landing. The default is set exclusively via the separate
// "Default landing" preference control (DefaultLandingPreference), so an admin can browse the other
// side temporarily without changing where they land on future visits.
export default function ProgramTypeSwitcher() {
    const { roles = [] } = useRoleAuthentication();
    const { pathname } = useLocation();

    const active = deriveProgramTypeFromPathname(pathname);

    // Hidden entirely unless the user has a role on BOTH sides (has543v2 — any dual role, not just admin).
    if (!hasDualProgramAccess(roles)) { return <React.Fragment />; }

    return (
        <>
            <Mui.ToggleButtonGroup
                size="small"
                exclusive
                value={active}
                aria-label="Program type"
                data-testid="program-type-switcher"
            >
                {/* programSwitch state signals a deliberate switch to Honors so the "/" (Dashboard) landing
                    guard does NOT re-apply a Scholars default-landing preference and bounce the user back
                    to /scholars/queue. Fresh login / refresh carry no such state, so the preference still
                    applies on a genuine landing (has543 follow-up). */}
                <Link to={HONORS_LANDING} state={{ programSwitch: true }} style={{ "marginRight": "0" }}>
                    <Mui.ToggleButton value={PROGRAMS.HONORS.KEY} data-testid="program-type-honors">{PROGRAMS.HONORS.label}</Mui.ToggleButton>
                </Link>
                <Link to={SCHOLARS_LANDING}>
                    <Mui.ToggleButton value={PROGRAMS.SCHOLARS.KEY} data-testid="program-type-scholars">{PROGRAMS.SCHOLARS.label}</Mui.ToggleButton>
                </Link>
            </Mui.ToggleButtonGroup>
            <Mui.Box aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                {`${active === PROGRAMS.SCHOLARS.KEY ? PROGRAMS.SCHOLARS.label : PROGRAMS.HONORS.label} selected`}
            </Mui.Box>
        </>
    );
}
